import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { logRedactedError } from '@/lib/telemetry/logError';
import { RefreshLinear } from '@assessprep/icons/solar';
import { useCustomMutation, useTranslate } from '@refinedev/core';
import { useState } from 'react';
import { toast } from 'sonner';

/**
 * APL-10884 (v4 rebuild, APL-11850 Slice 6) — "Download Users List (Using
 * Older/Unsupported App)" export modal. Backend handler:
 *   POST /api/v1/users/export_old_app_users_to_excel
 * returns one of:
 *   { code: "success",  export_excel_url: "<cdn>" } → trigger download + close
 *   { code: "no_data",  export_excel_url: null    } → friendly toast, modal closes
 *   500                                            → red error toast
 * Data layer unchanged from the v1 page (shared, structural rule 1); only the
 * antd Modal/Checkbox/message view is rebuilt on Dialog/Checkbox/sonner.
 */
export const OldAppUsersExportDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  const [loading, setLoading] = useState(false);
  const [includeLatest, setIncludeLatest] = useState(false);

  const { mutate: requestOldAppUsersExport } = useCustomMutation();

  const closeDialog = () => {
    setLoading(false);
    setIncludeLatest(false);
    onOpenChange(false);
  };

  const handleExport = () => {
    setLoading(true);
    requestOldAppUsersExport(
      {
        url: 'users/export_old_app_users_to_excel.json',
        method: 'post',
        // Admin → backend infers org from current_user. Superadmin must pass
        // by_org_id explicitly; on this page they don't have an org picker, so
        // for now they hit the same admin-org default. If we need superadmin
        // multi-org export later, surface an org picker in the dialog.
        values: { include_latest: includeLatest },
      },
      {
        onSuccess: (res: any) => {
          setLoading(false);
          if (res?.data?.code === 'no_data') {
            toast.info(translate('OfflineApp.download_old_app_users_no_data'));
            onOpenChange(false);
            return;
          }
          const url = res?.data?.export_excel_url;
          if (!url) {
            toast.error(translate('OfflineApp.download_old_app_users_failed'));
            return;
          }
          // Trigger the download in one click — append a hidden anchor, click
          // it, then remove. The `download` attr is a hint; for cross-origin
          // S3 URLs the browser falls back to Content-Disposition / mime-type
          // (which our .xls upload sets correctly), so the file still saves.
          const link = document.createElement('a');
          link.href = url;
          link.rel = 'noopener noreferrer';
          link.setAttribute('download', '');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          closeDialog();
        },
        onError: (error: any) => {
          logRedactedError('Old-app users export failed', error);
          setLoading(false);
          toast.error(translate('OfflineApp.download_old_app_users_failed'));
        },
      }
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) closeDialog();
      }}
    >
      <DialogContent data-testid="download-old-app-users-dialog">
        <DialogHeader>
          <DialogTitle>{translate('OfflineApp.download_old_app_users')}</DialogTitle>
          <DialogDescription>
            {translate('OfflineApp.download_old_app_users_description')}
          </DialogDescription>
        </DialogHeader>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={includeLatest}
            disabled={loading}
            onCheckedChange={(checked) => setIncludeLatest(checked === true)}
            data-testid="include-latest-checkbox"
          />
          {translate('OfflineApp.old_app_users_include_latest_versions')}
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={closeDialog} data-testid="old-app-users-cancel">
            {translate('CommonText.cancel')}
          </Button>
          <Button onClick={handleExport} disabled={loading} data-testid="old-app-users-export">
            {loading && <RefreshLinear aria-hidden className="animate-spin" />}
            {translate('CommonText.export')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
